// Feature: features/super-admin-billing-and-user-activity.md
import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Flex,
  SimpleGrid,
  Heading,
  Text,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Select,
  Spinner,
  Alert,
  AlertIcon,
  HStack,
  Divider
} from '@chakra-ui/react';
import { ArrowBackIcon, RepeatIcon } from '@chakra-ui/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import { AppContext } from './AppContext';

const formatNumber = (val) => Number(val || 0).toLocaleString();

const SuperAdminBillingDashboard = () => {
  const { accessToken } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialMonth = searchParams.get('month') || '';
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [monthOptions, setMonthOptions] = useState([]);
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBillingReport = useCallback(async (monthParam) => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);

    try {
      const queryStr = monthParam ? `?month=${encodeURIComponent(monthParam)}` : '';
      const response = await callApi(
        `/admin/super-dashboard/billing${queryStr}`,
        accessToken,
        { method: 'GET' }
      );
      if (response.status === 403) {
        setError('Super-Admin permissions are required to view the Billing Command Center.');
        return;
      }
      if (!response.ok) {
        throw new Error('Failed to load monthly billing report.');
      }
      const data = await response.json();
      setBillingData(data.results || {});
      setMonthOptions(data.month_options || []);
      if (data.selected_month && data.selected_month !== monthParam) {
        setSelectedMonth(data.selected_month);
      }
    } catch (err) {
      console.error('Error fetching Super-Admin billing report:', err);
      setError(err.message || 'Failed to load billing metrics.');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchBillingReport(selectedMonth);
  }, [fetchBillingReport, selectedMonth]);

  const handleMonthChange = (e) => {
    const nextMonth = e.target.value;
    setSelectedMonth(nextMonth);
    setSearchParams(nextMonth ? { month: nextMonth } : {});
  };

  const selectedMonthLabel =
    monthOptions.find((m) => m.value === selectedMonth)?.label || selectedMonth || 'Prior Month';

  const nma = billingData?.nma || {};
  const mph = billingData?.mpowerhealth || {};
  const usnCases = billingData?.us_neuro_cases || { rows: [], totals: {} };
  const usnClaims = billingData?.us_neuro_claims || { rows: [], totals: {} };
  const nextservices = billingData?.nextservices || {};
  const theos = billingData?.theos || { rows: [], total_cases: 0 };

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Box maxW="container.xl" mx="auto" py={8} px={4}>
        {/* Header Bar */}
        <Flex
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          gap={4}
          mb={6}
          p={5}
          bg="white"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          shadow="sm"
        >
          <Box>
            <HStack spacing={3} mb={1}>
              <Button
                size="xs"
                leftIcon={<ArrowBackIcon />}
                variant="outline"
                colorScheme="gray"
                onClick={() => navigate('/super-admin-dashboard')}
              >
                System Pulse
              </Button>
              <Badge colorScheme="purple" fontSize="xs" px={2} py={0.5} borderRadius="md">
                SUPER-ADMIN BILLING
              </Badge>
            </HStack>
            <Heading size="lg" color="gray.800">
              Billing Command Center
            </Heading>
            <Text fontSize="sm" color="gray.600" mt={1}>
              Unified monthly case & claim counts across organizations for billing reconciliation ({selectedMonthLabel})
            </Text>
          </Box>

          <HStack spacing={3}>
            <Select
              size="md"
              w="210px"
              bg="white"
              fontWeight="semibold"
              borderColor="blue.300"
              value={selectedMonth}
              onChange={handleMonthChange}
            >
              {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.value})
                </option>
              ))}
            </Select>
            <Button
              size="md"
              leftIcon={<RepeatIcon />}
              colorScheme="blue"
              variant="outline"
              bg="white"
              onClick={() => fetchBillingReport(selectedMonth)}
              isLoading={loading}
            >
              Refresh
            </Button>
          </HStack>
        </Flex>

        {error && (
          <Alert status="error" borderRadius="md" mb={6}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {loading && !billingData ? (
          <Flex justify="center" align="center" py={20}>
            <Spinner size="xl" color="blue.500" />
          </Flex>
        ) : (
          <>
            {/* Row 1: NMA & MPOWERHealth */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
              {/* NMA Card */}
              <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
                <CardHeader bg="gray.50" py={3} px={5} borderBottom="1px solid" borderColor="gray.200">
                  <Flex justify="space-between" align="center">
                    <Heading size="sm" color="gray.800">
                      NMA Billing
                    </Heading>
                    <Badge colorScheme="blue">Org ID: {nma.org_id || 1}</Badge>
                  </Flex>
                </CardHeader>
                <CardBody py={6} px={5}>
                  <Stat textAlign="center" mb={4}>
                    <StatNumber fontSize="4xl" fontWeight="bold" color="gray.800">
                      {formatNumber(nma.distinct_case_count)}
                    </StatNumber>
                    <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider" mt={1}>
                      Distinct Patient Cases (user_ionm_cases)
                    </StatLabel>
                  </Stat>

                  <Divider my={3} />

                  <SimpleGrid columns={2} spacing={3} textAlign="center">
                    <Box bg="gray.50" p={2.5} borderRadius="md" border="1px solid" borderColor="gray.200">
                      <Text fontSize="lg" fontWeight="bold" color="gray.700">
                        {formatNumber(nma.total_external_cases)}
                      </Text>
                      <Text fontSize="2xs" color="gray.500" textTransform="uppercase">
                        Total External Cases
                      </Text>
                    </Box>
                    <Box bg="gray.50" p={2.5} borderRadius="md" border="1px solid" borderColor="gray.200">
                      <Text fontSize="lg" fontWeight="bold" color="green.600">
                        {formatNumber(nma.coded_external_cases)}
                      </Text>
                      <Text fontSize="2xs" color="gray.500" textTransform="uppercase">
                        Coded External Cases
                      </Text>
                    </Box>
                  </SimpleGrid>
                </CardBody>
              </Card>

              {/* MPOWERHealth Card */}
              <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
                <CardHeader bg="gray.50" py={3} px={5} borderBottom="1px solid" borderColor="gray.200">
                  <Flex justify="space-between" align="center">
                    <Heading size="sm" color="gray.800">
                      MPOWERHealth Billing
                    </Heading>
                    <Badge colorScheme="purple">Org ID: {mph.org_id || 203}</Badge>
                  </Flex>
                </CardHeader>
                <CardBody py={6} px={5} display="flex" flexDirection="column" justifyContent="center">
                  <SimpleGrid columns={2} spacing={4} textAlign="center" my="auto">
                    <Box p={4} bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.100">
                      <Stat>
                        <StatNumber fontSize="3xl" fontWeight="bold" color="blue.700">
                          {formatNumber(mph.total_cases)}
                        </StatNumber>
                        <StatLabel fontSize="xs" color="blue.600" textTransform="uppercase" mt={1}>
                          Total Cases Created
                        </StatLabel>
                        <StatHelpText mb={0} fontSize="2xs" color="gray.500">
                          external_cases in {selectedMonth}
                        </StatHelpText>
                      </Stat>
                    </Box>

                    <Box p={4} bg="green.50" borderRadius="lg" border="1px solid" borderColor="green.100">
                      <Stat>
                        <StatNumber fontSize="3xl" fontWeight="bold" color="green.700">
                          {formatNumber(mph.total_claims)}
                        </StatNumber>
                        <StatLabel fontSize="xs" color="green.600" textTransform="uppercase" mt={1}>
                          Total Claims Generated
                        </StatLabel>
                        <StatHelpText mb={0} fontSize="2xs" color="gray.500">
                          MPH_SURGICAL_ASSIST(_STRICT)
                        </StatHelpText>
                      </Stat>
                    </Box>
                  </SimpleGrid>
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Row 2: US Neuro Cases & US Neuro Claims */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
              {/* US Neuro Cases by Extracted Org */}
              <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
                <CardHeader bg="gray.50" py={3} px={5} borderBottom="1px solid" borderColor="gray.200">
                  <Flex justify="space-between" align="center">
                    <Box>
                      <Heading size="sm" color="gray.800">
                        US Neuro Billing (Cases by Extracted Org)
                      </Heading>
                      <Text fontSize="2xs" color="gray.500">
                        Grouped by technician_report technical_report_org
                      </Text>
                    </Box>
                    <Badge colorScheme="teal">Org ID: {usnCases.org_id || 155}</Badge>
                  </Flex>
                </CardHeader>
                <CardBody p={0}>
                  {(!usnCases.rows || usnCases.rows.length === 0) ? (
                    <Box py={10} textAlign="center">
                      <Text fontSize="2xl" fontWeight="bold" color="gray.400">0</Text>
                      <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                        No US Neuro Cases in {selectedMonth}
                      </Text>
                    </Box>
                  ) : (
                    <Box overflowX="auto">
                      <Table size="sm" variant="simple">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th>Extracted Org</Th>
                            <Th isNumeric>Coded</Th>
                            <Th isNumeric>Uncoded</Th>
                            <Th isNumeric>Total</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {usnCases.rows.map((row) => (
                            <Tr key={row.extracted_org} _hover={{ bg: 'gray.50' }}>
                              <Td fontWeight="medium" fontSize="xs">
                                {row.extracted_org}
                              </Td>
                              <Td isNumeric fontSize="xs" color="green.600">
                                {formatNumber(row.coded_case_count)}
                              </Td>
                              <Td
                                isNumeric
                                fontSize="xs"
                                color={row.uncoded_case_count > 0 ? 'orange.600' : 'gray.500'}
                              >
                                {formatNumber(row.uncoded_case_count)}
                              </Td>
                              <Td isNumeric fontSize="xs" fontWeight="bold">
                                {formatNumber(row.total_case_count)}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                        <Tfoot bg="gray.100">
                          <Tr fontWeight="bold">
                            <Td fontSize="xs">Grand Total</Td>
                            <Td isNumeric fontSize="xs" color="green.700">
                              {formatNumber(usnCases.totals?.coded_case_count)}
                            </Td>
                            <Td isNumeric fontSize="xs" color="orange.700">
                              {formatNumber(usnCases.totals?.uncoded_case_count)}
                            </Td>
                            <Td isNumeric fontSize="xs" color="gray.900">
                              {formatNumber(usnCases.totals?.total_case_count)}
                            </Td>
                          </Tr>
                        </Tfoot>
                      </Table>
                    </Box>
                  )}
                </CardBody>
              </Card>

              {/* US Neuro Claims by Extracted Org */}
              <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
                <CardHeader bg="gray.50" py={3} px={5} borderBottom="1px solid" borderColor="gray.200">
                  <Flex justify="space-between" align="center">
                    <Box>
                      <Heading size="sm" color="gray.800">
                        US Neuro Billing (Claims by Extracted Org)
                      </Heading>
                      <Text fontSize="2xs" color="gray.500">
                        USN_COMPREHENSIVE_IONM (is_current = true) Pro & Tech claims
                      </Text>
                    </Box>
                    <Badge colorScheme="teal">Org ID: {usnClaims.org_id || 155}</Badge>
                  </Flex>
                </CardHeader>
                <CardBody p={0}>
                  {(!usnClaims.rows || usnClaims.rows.length === 0) ? (
                    <Box py={10} textAlign="center">
                      <Text fontSize="2xl" fontWeight="bold" color="gray.400">0</Text>
                      <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                        No US Neuro Claims in {selectedMonth}
                      </Text>
                    </Box>
                  ) : (
                    <Box overflowX="auto">
                      <Table size="sm" variant="simple">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th>Extracted Org</Th>
                            <Th isNumeric>Pro Claims</Th>
                            <Th isNumeric>Tech Claims</Th>
                            <Th isNumeric>Total Claims</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {usnClaims.rows.map((row) => (
                            <Tr key={row.extracted_org} _hover={{ bg: 'gray.50' }}>
                              <Td fontWeight="medium" fontSize="xs">
                                {row.extracted_org}
                              </Td>
                              <Td isNumeric fontSize="xs">
                                {formatNumber(row.pro_claims)}
                              </Td>
                              <Td isNumeric fontSize="xs">
                                {formatNumber(row.tech_claims)}
                              </Td>
                              <Td isNumeric fontSize="xs" fontWeight="bold">
                                {formatNumber(row.total_claims)}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                        <Tfoot bg="gray.100">
                          <Tr fontWeight="bold">
                            <Td fontSize="xs">Grand Total</Td>
                            <Td isNumeric fontSize="xs">
                              {formatNumber(usnClaims.totals?.pro_claims)}
                            </Td>
                            <Td isNumeric fontSize="xs">
                              {formatNumber(usnClaims.totals?.tech_claims)}
                            </Td>
                            <Td isNumeric fontSize="xs" color="gray.900">
                              {formatNumber(usnClaims.totals?.total_claims)}
                            </Td>
                          </Tr>
                        </Tfoot>
                      </Table>
                    </Box>
                  )}
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Legacy / Other Billing */}
            <Divider mb={6} />
            <Heading size="sm" color="gray.600" mb={4}>
              Legacy / Other Organization Billing
            </Heading>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
              {/* NextServices */}
              <Card shadow="xs" border="1px solid" borderColor="gray.200" bg="white">
                <CardHeader bg="gray.50" py={2.5} px={4} borderBottom="1px solid" borderColor="gray.200">
                  <Flex justify="space-between" align="center">
                    <Heading size="xs" color="gray.700">
                      NextServices Billing
                    </Heading>
                    <Badge colorScheme="gray">Org ID: {nextservices.org_id || 154}</Badge>
                  </Flex>
                </CardHeader>
                <CardBody py={4} textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="gray.700">
                    {formatNumber(nextservices.case_count)}
                  </Text>
                  <Text fontSize="2xs" color="gray.500" textTransform="uppercase">
                    Total External Cases
                  </Text>
                </CardBody>
              </Card>

              {/* Theos */}
              <Card shadow="xs" border="1px solid" borderColor="gray.200" bg="white">
                <CardHeader bg="gray.50" py={2.5} px={4} borderBottom="1px solid" borderColor="gray.200">
                  <Flex justify="space-between" align="center">
                    <Heading size="xs" color="gray.700">
                      Theos Billing (Synthetic Notes)
                    </Heading>
                    <Badge colorScheme="gray">Org IDs: 3, 5</Badge>
                  </Flex>
                </CardHeader>
                <CardBody py={4}>
                  {(!theos.rows || theos.rows.length === 0) ? (
                    <Box textAlign="center">
                      <Text fontSize="2xl" fontWeight="bold" color="gray.700">0</Text>
                      <Text fontSize="2xs" color="gray.500" textTransform="uppercase">
                        Synthetic Note Cases
                      </Text>
                    </Box>
                  ) : (
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th fontSize="2xs">Org ID</Th>
                          <Th fontSize="2xs" isNumeric>Distinct Cases</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {theos.rows.map((r) => (
                          <Tr key={r.org_id}>
                            <Td fontSize="xs">Org {r.org_id}</Td>
                            <Td fontSize="xs" isNumeric fontWeight="bold">
                              {formatNumber(r.distinct_case_count)}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  )}
                </CardBody>
              </Card>
            </SimpleGrid>

            <Alert status="info" borderRadius="md" variant="subtle">
              <AlertIcon />
              <Text fontSize="xs">
                Counts are queried in real-time from the Core database for calendar month{' '}
                <Text as="span" fontWeight="bold">{selectedMonthLabel} ({selectedMonth})</Text>.
              </Text>
            </Alert>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SuperAdminBillingDashboard;
